import { capitalize } from "lodash";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

export const EXPERIENCE_SORT_RANK: Record<string, number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2,
  experienced: 3,
};

export const experienceSortFunction = ({
  data,
  order,
}: {
  data: Array<{ developerProfile?: { experience?: string } }>;
  order: number | null | undefined;
}) =>
  [...data].sort((a, b) => {
    const aRank = EXPERIENCE_SORT_RANK[a.developerProfile?.experience ?? ""] ?? -1;
    const bRank = EXPERIENCE_SORT_RANK[b.developerProfile?.experience ?? ""] ?? -1;
    return (order ?? 1) * (aRank - bRank);
  });

export const nullLastStringSort =
  (fieldPath: string) =>
  ({ data, order }: { data: unknown[]; order: number | null | undefined }) =>
    [...data].sort((a, b) => {
      const getValue = (obj: unknown): string =>
        (fieldPath
          .split(".")
          .reduce((o: unknown, k) => (o as Record<string, unknown>)?.[k], obj) as string) ?? "";
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      return (order ?? 1) * aVal.localeCompare(bVal);
    });

export const toolingSortFunction = ({
  data,
  order,
}: {
  data: Array<{ developerProfile?: { topics?: string[] } }>;
  order: number | null | undefined;
}) =>
  [...data].sort((a, b) => {
    const aTopics = a.developerProfile?.topics;
    const bTopics = b.developerProfile?.topics;
    const isEmpty = (t?: string[]) => !t || t.length === 0;
    const isOtherOnly = (t?: string[]) => !!t?.length && t.every(v => v.toLowerCase() === "other");
    if (isEmpty(aTopics) && isEmpty(bTopics)) return 0;
    if (isEmpty(aTopics)) return 1;
    if (isEmpty(bTopics)) return -1;
    if (isOtherOnly(aTopics) && isOtherOnly(bTopics)) return 0;
    if (isOtherOnly(aTopics)) return 1;
    if (isOtherOnly(bTopics)) return -1;
    return (
      (order ?? 1) *
      (aTopics?.[0] ?? "").toLowerCase().localeCompare((bTopics?.[0] ?? "").toLowerCase())
    );
  });

// Same empty-last / other-second-to-last ordering as toolingSortFunction,
// generalized for the flag-gated multi-value columns (toolingSortFunction is
// deliberately left as-is so ungated code is untouched by this feature)
const multiValueSortFunction =
  <T>(getValues: (row: T) => string[] | undefined) =>
  ({ data, order }: { data: T[]; order: number | null | undefined }) =>
    [...data].sort((a, b) => {
      const aValues = getValues(a);
      const bValues = getValues(b);
      const isEmpty = (t?: string[]) => !t || t.length === 0;
      const isOtherOnly = (t?: string[]) =>
        !!t?.length && t.every(v => v.toLowerCase() === "other");
      if (isEmpty(aValues) && isEmpty(bValues)) return 0;
      if (isEmpty(aValues)) return 1;
      if (isEmpty(bValues)) return -1;
      if (isOtherOnly(aValues) && isOtherOnly(bValues)) return 0;
      if (isOtherOnly(aValues)) return 1;
      if (isOtherOnly(bValues)) return -1;
      return (
        (order ?? 1) *
        (aValues?.[0] ?? "").toLowerCase().localeCompare((bValues?.[0] ?? "").toLowerCase())
      );
    });

export const aiStackCategorySortFunction = (categoryKey: string) =>
  multiValueSortFunction<{ developerProfile?: { aiStack?: AiStack } }>(
    row => row.developerProfile?.aiStack?.[categoryKey]
  );

export const additionalLanguagesSortFunction = multiValueSortFunction<{
  developerProfile?: { additionalLanguages?: string[] };
}>(row => row.developerProfile?.additionalLanguages);

// ---------------------------------------------------------------------------
// AI stack (categorized) helpers
// ---------------------------------------------------------------------------

type AiStack = Record<string, string[] | undefined>;

export const DOES_NOT_USE_AI_OPTION = {
  label: "I do not use AI",
  value: DeveloperProfileConstants.VALUES.DOES_NOT_USE_AI,
};

// Column/filter metadata for the four AI stack categories. Headers get an
// "AI" prefix so they read unambiguously next to non-AI columns like Tooling.
export const AI_STACK_COLUMNS = DeveloperProfileConstants.OPTIONS.aiToolCategories.map(
  category => ({
    key: category.key,
    field: `developerProfile.aiStack.${category.key}`,
    header: `AI ${category.label}`,
    // lowercase noun for filter placeholders ("All AI tools", "Select AI tools")
    filterNoun: `AI ${category.label.toLowerCase()}`,
    // the explicit opt-out is filterable in every category
    filterOptions: [...category.options, DOES_NOT_USE_AI_OPTION],
  })
);

// Field paths gated behind the Developer-profile-ai-updates flag: the AI stack
// columns plus additionalLanguages, which ships in the same release.
export const FLAG_GATED_PROFILE_FIELDS = new Set([
  ...AI_STACK_COLUMNS.map(c => c.field),
  "developerProfile.additionalLanguages",
]);

// Flattens an explicit AI opt-out (usesAi === false) into every category as a
// sentinel value, so the per-category table filters — which only see their own
// field's value — can offer "I do not use AI" like any other option.
export const normalizeAiStack = (profile: { aiStack?: AiStack; usesAi?: boolean }): AiStack =>
  Object.fromEntries(
    DeveloperProfileConstants.OPTIONS.aiToolCategories.map(category => [
      category.key,
      profile.usesAi === false
        ? [DOES_NOT_USE_AI_OPTION.value]
        : (profile.aiStack?.[category.key] ?? []).map(v => v.trim().toLowerCase()),
    ])
  );

// ---------------------------------------------------------------------------
// Column body render helpers (pure string functions — callers wrap with withSkeleton if needed)
// ---------------------------------------------------------------------------

type DeveloperProfile =
  | {
      jobFunction?: string;
      language?: string;
      experience?: string;
      expertise?: string;
      topics?: string[];
      aiStack?: AiStack;
      usesAi?: boolean;
      additionalLanguages?: string[];
    }
  | boolean
  | null
  | undefined;

// The backend may return `false` for users who have no developer profile.
// Coerce boolean to undefined so callers can pass DirectoryUserShape.developerProfile directly.
const toProfile = (dp: DeveloperProfile) => (typeof dp === "object" ? dp : undefined);

export const devProfileJobFunction = (dp: DeveloperProfile): string => {
  const val = toProfile(dp)?.jobFunction;
  if (!val) return "";
  return capitalize(val.replace(/_/g, " "));
};

export const devProfileLanguage = (dp: DeveloperProfile): string => {
  const lang = toProfile(dp)?.language;
  if (!lang) return "";
  return (
    DeveloperProfileConstants.OPTIONS.programmingLanguages.find(o => o.value === lang.toLowerCase())
      ?.label ?? capitalize(lang)
  );
};

export const devProfileExperience = (dp: DeveloperProfile): string => {
  const exp = toProfile(dp)?.experience;
  if (!exp) return "";
  const opt = DeveloperProfileConstants.OPTIONS.experienceLevels.find(o => o.value === exp);
  return opt?.label?.split(" of")[0]?.trim() ?? exp;
};

export const devProfileExpertise = (dp: DeveloperProfile): string => {
  const val = toProfile(dp)?.expertise;
  if (!val) return "";
  return (
    DeveloperProfileConstants.OPTIONS.technicalExpertise.find(o => o.value === val)?.label ??
    capitalize(val.replace(/_/g, " "))
  );
};

export const devProfileTopics = (dp: DeveloperProfile): string => {
  const profile = toProfile(dp);
  if (!profile?.topics?.length) return "";
  return profile.topics
    .map(
      (t: string) =>
        DeveloperProfileConstants.OPTIONS.architectureTools.find(o => o.value === t)?.label ?? t
    )
    .join(", ");
};

export const devProfileAiStackCategory = (dp: DeveloperProfile, categoryKey: string): string => {
  const values = toProfile(dp)?.aiStack?.[categoryKey];
  if (!values?.length) return "";
  const options =
    DeveloperProfileConstants.OPTIONS.aiToolCategories.find(c => c.key === categoryKey)?.options ??
    [];
  return values
    .map(v =>
      v === DOES_NOT_USE_AI_OPTION.value
        ? DOES_NOT_USE_AI_OPTION.label
        : (options.find(o => o.value === v)?.label ?? v)
    )
    .join(", ");
};

export const devProfileAdditionalLanguages = (dp: DeveloperProfile): string => {
  const profile = toProfile(dp);
  if (!profile?.additionalLanguages?.length) return "";
  return profile.additionalLanguages
    .map(
      (t: string) =>
        DeveloperProfileConstants.OPTIONS.programmingLanguages.find(o => o.value === t)?.label ?? t
    )
    .join(", ");
};
